// src/Pages/ClientDetail.jsx
import React, { useState, useEffect, Suspense, useCallback } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import { Box, Grid, Paper, Typography, TextField, Button, Checkbox, IconButton, LinearProgress} from "@mui/material";
import { FilterList as FilterListIcon, Add as AddIcon, Delete as DeleteIcon} from "@mui/icons-material";
import { FaFileExcel } from "react-icons/fa";

// Lazy load
const ClientCard = React.lazy(() => import("../components/ClientCard"));
const path = `http://192.168.1.248:3103/client`;

function FilterPanel({ open, onApply, onCancel, initial }) {
    const [filters, setFilters] = useState(initial);
    const handleChange = (key) => (e) => { setFilters((f) => ({ ...f, [key]: e.target.value })); };

    if (!open) return null;

    return (
        <Paper sx={{ bgcolor: "#f5f7fa", p: 3, mt: 2, borderRadius: 1.5 }}>
            <Grid container spacing={2}>
                {[
                    { key: "gstNo", label: "GST No." },
                    { key: "companyName", label: "Company Name" },
                    { key: "contactPerson", label: "Contact Person" },
                    { key: "mobileNo", label: "Mobile No." },
                    { key: "pinCode", label: "Pincode" },
                    { key: "city", label: "City" },
                    { key: "state", label: "State" },
                    { key: "country", label: "Country" },
                ].map(({ key, label }) => (
                    <Grid item xs={12} md={6} key={key}>
                        <TextField fullWidth size="small" label={label} value={filters[key]} onChange={handleChange(key)} />
                    </Grid>
                ))}
            </Grid>
            <Box sx={{ mt: 3, display: "flex", justifyContent: "center", gap: 2 }}>
                <Button variant="contained" onClick={() => onApply(filters)}>
                    Search
                </Button>
                <Button variant="outlined" onClick={onCancel}>
                    Cancel
                </Button>
            </Box>
        </Paper>
    );
}

function ClientList({ filters, onDeleteBulk, exportTrigger }) {
    const [items, setItems] = useState([]);
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(new Set());

    const loadPage = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                mode: "search",
                offset,
                limit: 10,
                gstNumber: filters.gstNo,
                clientname: filters.companyName,
                contactPerson: filters.contactPerson,
                mobileNo: filters.mobileNo,
                pinCode: filters.pinCode,
                city: filters.city,
                state: filters.state,
                country: filters.country,
            };
            const { data } = await axios.get(`${path}/search`, { params });
            setItems((prev) => (offset === 0 ? data : [...prev, ...data]));
        } catch (error) {
            console.error("Error loading page:", error);
        } finally {
            setLoading(false);
        }
    }, [offset, filters]);

    useEffect(() => {
        setOffset(0);
        setItems([]);
    }, [filters]);

    useEffect(() => {
        loadPage();
    }, [offset, loadPage]);

    // useEffect(() => {
    //     axios.get(`${path}/count`).then((res) => setTotal(Number(res.data))).catch(console.error);
    // }, []);

    const toggle = (id) => {
        setSelected((prev) => {
            const updated = new Set(prev);
            updated.has(id) ? updated.delete(id) : updated.add(id);
            return updated;
        });
    };

    const selectAll = () => {
        setSelected((prev) => {
            const allIds = items.map((i) => i.id);
            return prev.size === items.length ? new Set() : new Set(allIds);
        });
    };

    useEffect(() => {
        if (exportTrigger) {
            axios.get(`${path}/search`, { params: { mode: "export" }, responseType: "blob", })
                .then((res) => {
                    const url = URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = "clients.xlsx";
                    link.click();
                });
        }
    }, [exportTrigger]);

    return (
        <>
            <Box sx={{ my: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <Checkbox checked={selected.size > 0 && selected.size === items.length} onChange={selectAll} />
                <IconButton color="error" disabled={selected.size === 0} onClick={() => onDeleteBulk(Array.from(selected))} >
                    <DeleteIcon />
                </IconButton>
                <Box flexGrow={1} />
                <Typography variant="subtitle2" color="text.secondary">
                    {items.length}/{total}
                </Typography>
            </Box>

            {items.map((c, i) => (
                <Suspense key={c.id} fallback={<LinearProgress />}>
                    <ClientCard client={c} index={i} selected={selected.has(c.id)} onSelect={() => toggle(c.id)} />
                </Suspense>
            ))}

            {items.length < total && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Button variant="outlined" onClick={() => setOffset((o) => o + 10)} disabled={loading} >
                        {loading ? "Loading…" : "Show More"}
                    </Button>
                </Box>
            )}

            {!loading && items.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
                    No records found.
                </Typography>
            )}
        </>
    );
}

export default function ClientDetail({ loggedInUser, companyData }) {
    const [filters, setFilters] = useState({
        gstNo: "",
        companyName: "",
        contactPerson: "",
        mobileNo: "",
        pinCode: "",
        city: "",
        state: "",
        country: "",
    });

    const [showFilter, setShowFilter] = useState(false);
    const [exportTrigger, setExportTrigger] = useState(false);

    const handleApply = (newFilters) => {
        setFilters(newFilters);
        setShowFilter(false);
    };

    const handleCancel = () => setShowFilter(false);

    const handleDeleteBulk = async (ids) => {
        await axios.post(`${path}/delete`, { objAryId: ids });
        setFilters((f) => ({ ...f })); // refresh
    };

    return (
        <Box sx={{ p: 3 }}>
            <Paper
                sx={{
                    p: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => setShowFilter((s) => !s)}>
                        <FilterListIcon />
                    </IconButton>

                    <IconButton component={RouterLink} to="/main/newClient" color="primary">
                        <AddIcon />
                    </IconButton>

                    <IconButton onClick={() => setExportTrigger((prev) => !prev)} color="success" >
                        <FaFileExcel />
                    </IconButton>
                </Box>
            </Paper>
            <FilterPanel open={showFilter} initial={filters} onApply={handleApply} onCancel={handleCancel} />
            <ClientList filters={filters} onDeleteBulk={handleDeleteBulk} exportTrigger={exportTrigger} />
        </Box>
    );
}